import { useCallback, useMemo } from 'react';
import { USD_DECIMALS } from '../constants';
import { convertBidsAsksToDepthData, DepthData, getDepthScale } from '../lib/depthDataHelpers';
import { formatNumber } from '../lib/formatters';
import { Gwei } from '../lib/gwei';
import { getDecimalPlaces, multiply } from '../lib/math';
import { groupOrderbook } from '../lib/orderbookHelpers';
import { PrettyPick } from '../lib/tsUtilityTypes';
import { BidsAsks } from '../types/orderbook';
import { useOpenOrderPrices } from './useOpenOrderPrices';
import { ProductType } from './useProducts';
import { usePropsAsState } from './usePropAsState';

/** Product fields required by orderbook */
export type OrderbookProduct = PrettyPick<
  ProductType,
  'baseTokenName' | 'quoteTokenName' | 'id' | 'tickSize' | 'lotSize'
>;

export const useOrderbookData = (
  data: BidsAsks | undefined,
  product: OrderbookProduct,
  numberOfRows: number
) => {
  const { baseTokenName, quoteTokenName, id: productId, tickSize, lotSize } = product;
  const [asset, setAsset] = usePropsAsState(baseTokenName);
  const [grouping, setGrouping] = usePropsAsState(Gwei.fmt(tickSize));

  // Apply price grouping
  const groupedData = useMemo(
    () => data && groupOrderbook(data, Gwei.toGwei(grouping)),
    [data, grouping]
  );
  const groupedMarketDepth = useMemo(
    () => groupedData && convertBidsAsksToDepthData(groupedData),
    [groupedData]
  );

  const { askPrices: accountAskPrices, bidPrices: accountBidPrices } = useOpenOrderPrices(
    productId,
    grouping
  );

  const marketDepthByPrice = useMemo(
    () =>
      groupedMarketDepth?.reduce<{
        ask: Record<string, DepthData>;
        bid: Record<string, DepthData>;
      }>(
        (acc, curr) => {
          acc[curr.type][Gwei.fmt(curr.price)] = curr;
          return acc;
        },
        { ask: {}, bid: {} }
      ),
    [groupedMarketDepth]
  );

  const asks = groupedData?.asks.slice(-numberOfRows);
  const bids = groupedData?.bids.slice(0, numberOfRows);
  const decimals = getDecimalPlaces(lotSize);
  const priceDecimals = getDecimalPlaces(tickSize);
  const depthScale = useMemo(
    () => getDepthScale(bids, asks, groupedMarketDepth),
    [bids, asks, groupedMarketDepth]
  );

  const getWidthAndDepth = useCallback(
    (price: bigint | undefined, type: 'bid' | 'ask') => {
      if (price === undefined) {
        return {
          depth: undefined,
          width: '',
        };
      }
      const depth = marketDepthByPrice?.[type][Gwei.fmt(price)]?.depth || 0n;
      const width = `${Number(Gwei.fmt(depthScale(depth))) * 100}%`;
      return {
        depth,
        width,
      };
    },
    [marketDepthByPrice, depthScale]
  );

  const formatRowValue = useCallback(
    (num: bigint | undefined, price: bigint | undefined) => {
      if (num === undefined || price === undefined) {
        return '-';
      }
      const decimalsToUse = asset === quoteTokenName ? USD_DECIMALS : decimals;
      return formatNumber(asset === quoteTokenName ? multiply(num, price) : num, {
        maximumFractionDigits: decimalsToUse,
        minimumFractionDigits: decimalsToUse,
      });
    },
    [asset, decimals, quoteTokenName]
  );

  return {
    asset,
    setAsset,
    grouping,
    setGrouping,
    asks,
    bids,
    priceDecimals,
    accountAskPrices,
    accountBidPrices,
    formatRowValue,
    getWidthAndDepth,
  };
};
